import { Injectable, OnDestroy } from '@angular/core';

import { map, take } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { Api } from '../../../../shared/constants/api';
import { ILastVisitedLocation, IUsersLastVisitedLocation } from '../../interfaces/last-visited.interface';
import { BrowserLocalStorageService, LOCAL_STORAGE } from '../browser-local-storage.service';
import { RequiredSecurities } from '../../../../shared/constants/required-securities';
import { UserAclService } from '../user-acl.service';
import { AclSecurityAdapter } from '../acl.service';

@Injectable({
  providedIn: 'root',
})
export class LastVisitedLocationService implements OnDestroy {
  private hasAccessToDashboard$: Observable<boolean>;
  private subscriptions: Subscription;

  constructor(
    private browserLocalStorageService: BrowserLocalStorageService,
    private userAclService: UserAclService,
  ) {
    this.hasAccessToDashboard$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useFunctionalActions(RequiredSecurities.GR_Governance_Read.functionalActions)
            .useOptions({ requireFunctionalActions: true })
            .useRequiredSecurity(RequiredSecurities.GR_Governance_Read, false)
            .check(),
        ),
      );

    this.subscriptions = new Subscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public saveLastVisitedLocation(userId: string, location: ILastVisitedLocation): void {
    if (
      this.isOrganizationRoute(location.path) ||
      this.isWorkspaceRoute(location.path) ||
      this.isDigitalAssetRoute(location.path)
    ) {
      this.subscriptions.add(
        this.getLandingLocation(location).subscribe((landingLocation: ILastVisitedLocation) => {
          const existingLocations: IUsersLastVisitedLocation =
            this.browserLocalStorageService.getItem(LOCAL_STORAGE.LAST_VISITED_LOCATION) || {};
          const updatedLocations: IUsersLastVisitedLocation = {
            ...existingLocations,
            [userId]: landingLocation,
          };

          this.browserLocalStorageService.setItem(LOCAL_STORAGE.LAST_VISITED_LOCATION, updatedLocations);
        }),
      );
    }
  }

  public getLastVisitedLocation(): ILastVisitedLocation | null {
    const userId: string = this.browserLocalStorageService.getItem(LOCAL_STORAGE.USER_ID);
    const locations: IUsersLastVisitedLocation | null = this.browserLocalStorageService.getItem(
      LOCAL_STORAGE.LAST_VISITED_LOCATION,
    );

    return locations?.[userId] ?? null;
  }

  // Path redirection mappings
  private getLandingLocation(currentLocation: ILastVisitedLocation): Observable<ILastVisitedLocation> {
    if (currentLocation.path === `/${Api.portfolio}`) {
      return this.hasAccessToDashboard$.pipe(
        take(1),
        map(
          (hasAccess: boolean): ILastVisitedLocation => ({
            ...currentLocation,
            path: hasAccess ? `/${Api.overview}` : `/${Api.portfolio}`,
          }),
        ),
      );
    }
    return of(currentLocation);
  }

  private isDigitalAssetRoute(path: string): boolean {
    return [`/${Api.dashboard}`].includes(path);
  }

  private isWorkspaceRoute(path: string): boolean {
    return [`/${Api.overview}`, `/${Api.portfolio}`].includes(path);
  }

  private isOrganizationRoute(path: string): boolean {
    return [`/${Api.organization}`].includes(path);
  }
}
